import React, {createContext, useContext, useEffect, useState} from "react";
import {
    desistLogin,
    getLoggedInUser,
    isAdmin,
    isLoggedIn,
    persistLogin
} from "../../../Utilities/LocalStorage/storageUtilities";
import {isObjectNullOrEmpty} from "../../../Utilities/Types/objectUtilities";
import {userSearchModel} from "../../../Containers/User/userFactorySearch";
import {isValidUser} from "../../../Containers/User/userUtilities";
import {userModel} from "../../../Containers/User/userFactory";
import {studentSearchModel} from "../../../Containers/Student/studentSearchFactory";
import {userApi} from "../../../Containers/User/userApi";
import {useAppContext} from "./AppContext";
import {orderSearchModel} from "../../../Containers/Order/OrderManagement/orderManagementSearchFactory";
import {loanSearchModel} from "../../../Containers/Loan/Textbooks/LoanManagement/loanSearchFactory";
import {librarySearchModel} from "../../../Containers/Library/librarySearchFactory";
import {loanHistorySearchModel} from "../../../Containers/Loan/Textbooks/LoanHistory/loanHistorySearchFactory";
import {aphSearchModel} from "../../../Containers/Library/Aph/AphCatalog/aphSearchFactory";
import {aphLoanHistorySearchModel} from "../../../Containers/Loan/Aph/LoanHistory/aphLoanHistorySearchFactory";
import {aphLoanSearchModel} from "../../../Containers/Loan/Aph/LoanManagement/aphLoanSearchFactory";
import {loanLibraryCatalogSearchModel} from "../../../Containers/Library/LoanLibrary/LoanLibraryCatalog/loanLibraryCatalogSearchFactory";
import {loanLibraryHistorySearchModel} from "../../../Containers/Library/LoanLibrary/LoanLibraryHistory/loanLibraryHistorySearchFactory";
import {loanLibraryLoanSearchModel} from "../../../Containers/Loan/LoanLibrary/LoanManagement/loanLibraryLoanSearchFactory";
import {braillerCatalogSearchModel} from "../../../Containers/Library/Braillers/BraillerCatalog/braillerCatalogSearchFactory";
import {braillerHistorySearchModel} from "../../../Containers/Library/Braillers/BraillerHistory/braillerHistorySearchFactory";
import {braillerLoanSearchModel} from "@containers/Library/Braillers/BraillerLoanManagement/braillerLoanManagementSearchFactory";

import type {SearchModel} from "../../../Utilities/Search/searchFactory";

export interface UserContextValue {
    user: Record<string, unknown>;
    userId: string;
    isAdmin: boolean;
    isLoggedIn: boolean;
    fetchUser: () => Promise<unknown>;
    setLoggedInFalse: () => boolean;
    setLoggedInTrue: (user: unknown) => boolean;
    [key: string]: unknown;
}

export const UserContext = createContext<UserContextValue>({} as UserContextValue);
export const useUserContext = (): UserContextValue => useContext(UserContext);

interface UserContextProviderProps {
    children: React.ReactNode;
}

export const UserContextProvider: React.FC<UserContextProviderProps> = ({children}) => {
    const [user, setUser] = useState(userModel());
    const {handleApiCall} = useAppContext();

    const isUserAdmin = isAdmin();
    const isUserLoggedIn = isLoggedIn();
    const userId = getLoggedInUser();
    const searchProps: [string] = [userId];

    const updateSearchCriteria = (userId: string, searchCriteria: SearchModel | null, setSearchCriteria: (val: SearchModel) => void, searchModel: (id: string) => SearchModel) => {
        if (!userId) return;

        const searchCriteriaUserId = searchCriteria ? searchCriteria.pageId : userId.toString();

        if ((isObjectNullOrEmpty(searchCriteria) || searchCriteria?.pageId !== searchCriteriaUserId)) {
            setSearchCriteria(searchModel(userId));
        }
    }

    const [userSearchCriteria, setUserSearchCriteria] = useState<SearchModel | null>(null);
    const handleSetUserSearchCriteria = (criteria: SearchModel) => setUserSearchCriteria(criteria);
    const updateUserSearchCriteria = () => updateSearchCriteria(...searchProps, userSearchCriteria,
        setUserSearchCriteria, userSearchModel);

    const [studentSearchCriteria, setStudentSearchCriteria] = useState<SearchModel | null>(null);
    const handleSetStudentSearchCriteria = (criteria: SearchModel) => setStudentSearchCriteria(criteria);
    const updateStudentSearchCriteria = () => updateSearchCriteria(...searchProps, studentSearchCriteria,
        setStudentSearchCriteria, studentSearchModel);

    const [orderSearchCriteria, setOrderSearchCriteria] = useState<SearchModel | null>(null);
    const handleSetOrderSearchCriteria = (criteria: SearchModel) => setOrderSearchCriteria(criteria);
    const updateOrderSearchCriteria = () => updateSearchCriteria(...searchProps, orderSearchCriteria,
        setOrderSearchCriteria, orderSearchModel);

    //loan
    const [loanSearchCriteria, setLoanSearchCriteria] = useState<SearchModel | null>(null);
    const handleSetLoanSearchCriteria = (criteria: SearchModel) => setLoanSearchCriteria(criteria);
    const updateLoanSearchCriteria = () => updateSearchCriteria(...searchProps, loanSearchCriteria,
        setLoanSearchCriteria, loanSearchModel);

    //loan history
    const [loanHistorySearchCriteria, setLoanHistorySearchCriteria] = useState<SearchModel | null>(null);
    const handleSetLoanHistorySearchCriteria = (criteria: SearchModel) => setLoanHistorySearchCriteria(criteria);
    const updateLoanHistorySearchCriteria = () => updateSearchCriteria(...searchProps, loanHistorySearchCriteria,
        setLoanHistorySearchCriteria, loanHistorySearchModel);

    //library
    const [librarySearchCriteria, setLibrarySearchCriteria] = useState<SearchModel | null>(null);
    const handleSetLibrarySearchCriteria = (criteria: SearchModel) => setLibrarySearchCriteria(criteria);
    const updateLibrarySearchCriteria = () => updateSearchCriteria(...searchProps, librarySearchCriteria,
        setLibrarySearchCriteria, librarySearchModel);

    //aph
    const [aphSearchCriteria, setAphSearchCriteria] = useState<SearchModel | null>(null);
    const handleSetAphSearchCriteria = (criteria: SearchModel) => setAphSearchCriteria(criteria);
    const updateAphSearchCriteria = () => updateSearchCriteria(...searchProps, aphSearchCriteria,
        setAphSearchCriteria, aphSearchModel);

    //aph loan
    const [aphLoanSearchCriteria, setAphLoanSearchCriteria] = useState<SearchModel | null>(null);
    const handleSetAphLoanSearchCriteria = (criteria: SearchModel) => setAphLoanSearchCriteria(criteria);
    const updateAphLoanSearchCriteria = () => updateSearchCriteria(...searchProps, aphLoanSearchCriteria,
        setAphLoanSearchCriteria, aphLoanSearchModel);

    //loan library catalog
    const [loanLibraryCatalogSearchCriteria, setLoanLibraryCatalogSearchCriteria] = useState<SearchModel | null>(null);
    const handleSetLoanLibraryCatalogSearchCriteria = (criteria: SearchModel) => setLoanLibraryCatalogSearchCriteria(criteria);
    const updateLoanLibraryCatalogSearchCriteria = () => updateSearchCriteria(...searchProps, loanLibraryCatalogSearchCriteria,
        setLoanLibraryCatalogSearchCriteria, loanLibraryCatalogSearchModel);

    //aph loan history
    const [aphLoanHistorySearchCriteria, setAphLoanHistorySearchCriteria] = useState<SearchModel | null>(null);
    const handleSetAphLoanHistorySearchCriteria = (criteria: SearchModel) => setAphLoanHistorySearchCriteria(criteria);
    const updateAphLoanHistorySearchCriteria = () => updateSearchCriteria(...searchProps, aphLoanHistorySearchCriteria,
        setAphLoanHistorySearchCriteria, aphLoanHistorySearchModel);


    //loan library history
    const [loanLibraryHistorySearchCriteria, setLoanLibraryHistorySearchCriteria] = useState<SearchModel | null>(null);
    const handleSetLoanLibraryHistorySearchCriteria = (criteria: SearchModel) => setLoanLibraryHistorySearchCriteria(criteria);
    const updateLoanLibraryHistorySearchCriteria = () => updateSearchCriteria(...searchProps, loanLibraryHistorySearchCriteria,
        setLoanLibraryHistorySearchCriteria, loanLibraryHistorySearchModel);

    //loan library loan management
    const [loanLibraryLoanSearchCriteria, setLoanLibraryLoanSearchCriteria] = useState<SearchModel | null>(null);
    const handleSetLoanLibraryLoanSearchCriteria = (criteria: SearchModel) => setLoanLibraryLoanSearchCriteria(criteria);
    const updateLoanLibraryLoanSearchCriteria = () => updateSearchCriteria(...searchProps, loanLibraryLoanSearchCriteria,
        setLoanLibraryLoanSearchCriteria, loanLibraryLoanSearchModel);


    //brailler catalog
    const [braillerCatalogSearchCriteria, setBraillerCatalogSearchCriteria] = useState<SearchModel>(braillerCatalogSearchModel(userId));
    const handleSetBraillerCatalogSearchCriteria = (criteria: SearchModel) => setBraillerCatalogSearchCriteria(criteria);
    const updateBraillerCatalogSearchCriteria = () => updateSearchCriteria(...searchProps, braillerCatalogSearchCriteria,
        setBraillerCatalogSearchCriteria, braillerCatalogSearchModel);

    //brailler loan history
    const [braillerHistorySearchCriteria, setBraillerHistorySearchCriteria] = useState<SearchModel | null>(null);
    const handleSetBraillerHistorySearchCriteria = (criteria: SearchModel) => setBraillerHistorySearchCriteria(criteria);
    const updateBraillerHistorySearchCriteria = () => updateSearchCriteria(...searchProps, braillerHistorySearchCriteria,
        setBraillerHistorySearchCriteria, braillerHistorySearchModel);

    //brailler loan management
    const [braillerLoanManagementSearchCriteria, setBraillerLoanManagementSearchCriteria] = useState<SearchModel>(braillerLoanSearchModel());
    const handleSetBraillerLoanManagementSearchCriteria = (searchCriteria: SearchModel) => {setBraillerLoanManagementSearchCriteria(searchCriteria);};
    const updateBraillerLoanManagementSearchCriteria = () => updateSearchCriteria(...searchProps, braillerLoanManagementSearchCriteria,
        setBraillerLoanManagementSearchCriteria, braillerLoanSearchModel);


    const setUserIfValid = (user: unknown) => {
        const validatedUser = userModel(user as Record<string, unknown>);
        if (isValidUser(validatedUser)) {
            setUser(validatedUser);
            return true;
        }

        return false;
    };

    const setLoggedInFalse = () => {
        desistLogin();
        setUser(userModel());
        return false;
    };

    const setLoggedInTrue = (user: unknown) => {
        const userData = user as Record<string, unknown>;
        if (!isValidUser(userData)) return setLoggedInFalse();
        persistLogin(
            userData.userId as string,
            userData.email as string,
            userData.fullName as string,
            userData.roles as unknown[],
            userData.canAssignDelegate as boolean,
            userData.patronRole,
            userData.patronSubType
        );
        setUser(userModel());

        if (userData.notifier) (userData.notifier as { notify: () => void }).notify();

        return true;
    };

    const fetchUser = () => {
        return handleApiCall(userApi.getUser, setUserIfValid, setLoggedInFalse);
    };


    useEffect(() => {
        updateOrderSearchCriteria();
        updateLibrarySearchCriteria();
        updateLoanSearchCriteria();
        updateLoanHistorySearchCriteria();
        updateStudentSearchCriteria();
        updateUserSearchCriteria();
        updateAphSearchCriteria();
        updateAphLoanSearchCriteria();
        updateAphLoanHistorySearchCriteria();
        updateLoanLibraryCatalogSearchCriteria();
        updateLoanLibraryHistorySearchCriteria();
        updateLoanLibraryLoanSearchCriteria();
        updateBraillerCatalogSearchCriteria();
        updateBraillerHistorySearchCriteria();
        updateBraillerLoanManagementSearchCriteria();
        // eslint-disable-next-line
    }, [userId]);


    return (<UserContext
            value={{
                aphLoanHistorySearchCriteria,
                aphLoanSearchCriteria,
                aphSearchCriteria,
                braillerCatalogSearchCriteria,
                braillerHistorySearchCriteria,
                braillerLoanManagementSearchCriteria,
                fetchUser,
                handleSetAphLoanHistorySearchCriteria,
                handleSetAphLoanSearchCriteria,
                handleSetAphSearchCriteria,
                handleSetBraillerCatalogSearchCriteria,
                handleSetBraillerHistorySearchCriteria,
                handleSetBraillerLoanManagementSearchCriteria,
                handleSetLibrarySearchCriteria,
                handleSetLoanHistorySearchCriteria,
                handleSetLoanLibraryCatalogSearchCriteria,
                handleSetLoanLibraryHistorySearchCriteria,
                handleSetLoanLibraryLoanSearchCriteria,
                handleSetLoanSearchCriteria,
                handleSetOrderSearchCriteria,
                handleSetStudentSearchCriteria,
                handleSetUserSearchCriteria,
                isAdmin: isUserAdmin,
                isLoggedIn: isUserLoggedIn,
                librarySearchCriteria,
                loanHistorySearchCriteria,
                loanLibraryCatalogSearchCriteria,
                loanLibraryHistorySearchCriteria,
                loanLibraryLoanSearchCriteria,
                loanSearchCriteria,
                orderSearchCriteria,
                setLoggedInFalse,
                setLoggedInTrue,
                studentSearchCriteria,
                user,
                userId,
                userSearchCriteria,
            }}>
            {children}
        </UserContext>)
};
